import { ApiResult, request, safeRequest } from '@synergy/api';

import {
    SearchCalibrationCase,
    SearchCuration,
    SearchCurationRule,
    SearchCurations,
    SearchDictionary,
    SearchDictionaryScope,
    SearchExplanation,
    SearchFunnel,
    SearchJudgementOverview,
    SearchJudgementVerdict,
    SearchPreview,
    SearchPreviewDraft,
    SearchQualityOverview,
    SearchWorklist,
    SearchWorklistStatus,
} from '../../features/configuration/search/models/search-quality';

export class SearchQualityApi {
    constructor(protected baseUrl: string) {}

    public getOverview = async (days: number): Promise<ApiResult<SearchQualityOverview>> => {
        return safeRequest(
            () => request.get(`${this.baseUrl}/search-quality/overview`, { params: { days } }),
            response => response.data
        );
    };

    public getFunnel = async (days: number): Promise<ApiResult<SearchFunnel>> => {
        return safeRequest(
            () => request.get(`${this.baseUrl}/search-quality/funnel`, { params: { days } }),
            response => response.data
        );
    };

    public getWorklist = async (days: number, limit: number): Promise<ApiResult<SearchWorklist>> => {
        return safeRequest(
            () => request.get(`${this.baseUrl}/search-quality/worklist`, { params: { days, limit } }),
            response => response.data
        );
    };

    public setWorklistStatus = async (term: string, status: SearchWorklistStatus): Promise<ApiResult<void>> => {
        return safeRequest(
            () => request.patch(`${this.baseUrl}/search-quality/worklist/status`, { term, status }),
            response => response.data
        );
    };

    public getJudgements = async (days: number): Promise<ApiResult<SearchJudgementOverview>> => {
        return safeRequest(
            () => request.get(`${this.baseUrl}/search-quality/judgements`, { params: { days } }),
            response => response.data
        );
    };

    public getCalibrationCase = async (term?: string): Promise<ApiResult<SearchCalibrationCase>> => {
        return safeRequest(
            () => request.get(`${this.baseUrl}/search-quality/calibration`, { params: term ? { term } : {} }),
            response => response.data
        );
    };

    public saveCalibration = async (
        term: string,
        searchEventId: number,
        verdicts: { productId: number; rank: number; verdict: SearchJudgementVerdict }[]
    ): Promise<ApiResult<void>> => {
        return safeRequest(
            () => request.post(`${this.baseUrl}/search-quality/calibration`, { term, searchEventId, verdicts }),
            response => response.data
        );
    };

    public explain = async (term: string): Promise<ApiResult<SearchExplanation>> => {
        return safeRequest(
            () => request.get(`${this.baseUrl}/search-quality/explain`, { params: { term } }),
            response => response.data
        );
    };

    public preview = async (term: string, draft: SearchPreviewDraft): Promise<ApiResult<SearchPreview>> => {
        return safeRequest(
            () => request.post(`${this.baseUrl}/search-quality/preview`, { term, ...draft }),
            response => response.data
        );
    };

    public getDictionary = async (): Promise<ApiResult<SearchDictionary>> => {
        return safeRequest(
            () => request.get(`${this.baseUrl}/search-quality/dictionary`),
            response => response.data
        );
    };

    public saveDictionaryEntry = async (
        term: string,
        replacement: string,
        scope: SearchDictionaryScope
    ): Promise<ApiResult<void>> => {
        return safeRequest(
            () => request.put(`${this.baseUrl}/search-quality/dictionary`, { term, replacement, scope }),
            response => response.data
        );
    };

    public rejectDictionaryEntry = async (term: string, scope: SearchDictionaryScope): Promise<ApiResult<void>> => {
        return safeRequest(
            () => request.post(`${this.baseUrl}/search-quality/dictionary/reject`, { term, scope }),
            response => response.data
        );
    };

    public removeDictionaryEntry = async (term: string, scope: SearchDictionaryScope): Promise<ApiResult<void>> => {
        return safeRequest(
            () => request.post(`${this.baseUrl}/search-quality/dictionary/remove`, { term, scope }),
            response => response.data
        );
    };

    public getCurations = async (): Promise<ApiResult<SearchCurations>> => {
        return safeRequest(
            () => request.get(`${this.baseUrl}/search-quality/curations`),
            response => response.data
        );
    };

    public getCuration = async (term: string): Promise<ApiResult<SearchCuration>> => {
        return safeRequest(
            () => request.get(`${this.baseUrl}/search-quality/curation`, { params: { term } }),
            response => response.data
        );
    };

    public saveCuration = async (
        term: string,
        productId: number,
        rule: SearchCurationRule,
        options: { position?: number; reason?: string; validUntil?: string } = {}
    ): Promise<ApiResult<void>> => {
        return safeRequest(
            () => request.put(`${this.baseUrl}/search-quality/curation`, { term, productId, rule, ...options }),
            response => response.data
        );
    };

    public removeCuration = async (term: string, productId: number): Promise<ApiResult<void>> => {
        return safeRequest(
            () => request.post(`${this.baseUrl}/search-quality/curation/remove`, { term, productId }),
            response => response.data
        );
    };
}
